import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../../config';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { fetchWithOrg } from '../../utils/api';
import { cleanOrgName } from '../../utils/formatters';
import InviteMemberModal from '../../components/InviteMemberModal';
import MemberDetailDrawer from '../../components/MemberDetailDrawer';
import { toast } from 'sonner';
import { getPendingPaymentNotification, savePendingPaymentNotification } from '../../hooks/usePaymentNotification';

import {
  ArrowCounterClockwise,
  CaretDown,
  CaretUp,
  Check,
  CircleNotch,
  CreditCard,
  CurrencyDollar,
  Envelope,
  Eye,
  Funnel,
  GearSix,
  MagnifyingGlass,
  Minus,
  Plus,
  Shield,
  Trash,
  UserPlus,
  UsersThree,
  WarningCircle,
  X
} from '@phosphor-icons/react';
/**
 * Team Members Page - Phase B: Seat-Based Monetisation
 * Manage team members, seats, and billing
 */
const TeamMembers = () => {
  const { activeOrganization } = useOrganization();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [billing, setBilling] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [updatingSeats, setUpdatingSeats] = useState(false);
  const [purchasedSeats, setPurchasedSeats] = useState(0);
  const [seatsToPurchase, setSeatsToPurchase] = useState(1);
  const [showPurchasePanel, setShowPurchasePanel] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const paymentProcessed = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [drawerMember, setDrawerMember] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  // Verify and process payment
  const verifyAndProcessPayment = useCallback(async (sessionId) => {
    setProcessingPayment(true);
    try {
      // Poll the checkout status to verify and process the payment
      const response = await fetch(
        `${BACKEND_URL}/api/stripe/checkout-status/${sessionId}`,
        {
          credentials: 'include',
          headers: {
            'X-Organization-Id': activeOrganization?.organization_id
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid' || data.completed) {
          toast.success('Seat purchase successful! Your team seats have been added.');
          // Refresh team data to show new seats
          fetchTeamData();
        } else {
          toast.error('Payment verification failed. Please contact support.');
        }
      } else {
        toast.error('Could not verify payment. Please refresh the page.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Error verifying payment. Please refresh the page.');
    } finally {
      setProcessingPayment(false);
    }
  }, [activeOrganization?.organization_id]);

  // Handle payment callback - robust implementation with localStorage
  useEffect(() => {
    // Skip if already processed in this session or org not loaded
    if (paymentProcessed.current || !activeOrganization?.organization_id) return;
    
    // First check localStorage for pending notification (survives page reload)
    const pending = getPendingPaymentNotification();
    if (pending && pending.type === 'success' && pending.purchaseType === 'seat') {
      paymentProcessed.current = true;
      verifyAndProcessPayment(pending.sessionId);
      return;
    }
    
    // Check URL params for payment callback
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const purchaseType = searchParams.get('type');
    
    if (paymentStatus === 'success' && sessionId && purchaseType === 'seat') {
      paymentProcessed.current = true;
      // Save to localStorage immediately in case of remount
      savePendingPaymentNotification('success', sessionId, purchaseType);
      // Clear URL params
      setSearchParams({});
      // Process payment
      verifyAndProcessPayment(sessionId);
    } else if (paymentStatus === 'cancelled') {
      paymentProcessed.current = true;
      setSearchParams({});
      toast.info('Payment was cancelled. No charges were made.');
    }
  }, [searchParams, setSearchParams, activeOrganization?.organization_id, verifyAndProcessPayment]);

  useEffect(() => {
    if (activeOrganization) {
      fetchTeamData();
    }
  }, [activeOrganization]);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/team`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || []);
        setBilling(data.billing);
        setPurchasedSeats(data.billing?.seats_purchased || 0);
      } else {
        // Don't show error if we're in the middle of switching organizations
        const isSwitching = sessionStorage.getItem('mm_org_switching') === 'true';
        if (!isSwitching) {
          showToast('Failed to load team data', 'error');
        }
      }
    } catch (err) {
      console.error('Error fetching team data:', err);
      // Don't show error if we're in the middle of switching organizations
      const isSwitching = sessionStorage.getItem('mm_org_switching') === 'true';
      if (!isSwitching) {
        showToast('Error loading team data', 'error');
      }
    } finally {
      setLoading(false);
      // Clear the switching flag after first load
      sessionStorage.removeItem('mm_org_switching');
    }
  };

  const handleInvite = async (inviteData) => {
    try {
      setInviting(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/invite`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(inviteData)
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        showToast('Invitation sent successfully', 'success');
        setShowInviteModal(false);
        fetchTeamData();
      } else {
        const error = await response.json();
        const errorMessage = typeof error === 'object' ? (error.detail || error.message || 'Failed to send invitation') : String(error);
        showToast(errorMessage, 'error');
      }
    } catch (err) {
      console.error('Error inviting user:', err);
      const errorMessage = err.message || 'Error sending invitation';
      showToast(errorMessage, 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleAssignSeat = async (userId) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/seats/assign`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId })
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        showToast('Seat assigned successfully', 'success');
        fetchTeamData();
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to assign seat', 'error');
      }
    } catch (err) {
      console.error('Error assigning seat:', err);
      showToast('Error assigning seat', 'error');
    }
  };

  const handleRemoveSeat = async (userId) => {
    if (!confirm('Are you sure you want to remove this seat? The employee will lose access to run agents and workflows.')) {
      return;
    }

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/seats/remove`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId })
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        showToast('Seat removed successfully', 'success');
        fetchTeamData();
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to remove seat', 'error');
      }
    } catch (err) {
      console.error('Error removing seat:', err);
      showToast('Error removing seat', 'error');
    }
  };

  const handleDeleteUser = async (userId, userName, userEmail) => {
    if (!confirm(`Are you sure you want to permanently remove ${userName || userEmail} from your organization? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/users/${userId}`,
        {
          method: 'DELETE'
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        showToast('User removed successfully', 'success');
        fetchTeamData();
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to remove user', 'error');
      }
    } catch (err) {
      console.error('Error removing user:', err);
      showToast('Error removing user', 'error');
    }
  };

  const handleUpdateSeats = async () => {
    if (seatsToPurchase < 1) {
      toast.error('Please select at least 1 seat to purchase');
      return;
    }

    try {
      setProcessingPayment(true);
      
      // Create Stripe checkout session for seat purchase
      const response = await fetch(
        `${BACKEND_URL}/api/stripe/create-seat-checkout`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Organization-Id': activeOrganization.organization_id
          },
          credentials: 'include',
          body: JSON.stringify({ 
            quantity: seatsToPurchase,
            origin_url: window.location.origin
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating seat checkout:', err);
      toast.error('Error starting checkout');
    } finally {
      setProcessingPayment(false);
    }
  };

  const showToast = (message, type = 'info') => {
    if (type === 'success') {
      toast.success(message);
    } else if (type === 'error') {
      toast.error(message);
    } else {
      toast.info(message);
    }
  };

  const handleRevokeInvite = async (memberId, memberEmail) => {
    if (!confirm(`Revoke invitation for ${memberEmail}? They will no longer be able to join.`)) return;
    setRevokingId(memberId);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/team/invitations/${memberId}`,
        { method: 'DELETE' },
        activeOrganization.organization_id
      );
      if (res.ok) {
        toast.success('Invitation revoked');
        fetchTeamData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to revoke invitation');
      }
    } catch (err) {
      toast.error('Error revoking invitation');
    } finally {
      setRevokingId(null);
    }
  };

  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        (m.name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q);
      const matchesRole = roleFilter === 'all' || m.role === roleFilter;
      const memberStatus = m.status || 'active';
      const matchesStatus = statusFilter === 'all' || memberStatus === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [members, searchQuery, roleFilter, statusFilter]);

  const getRoleBadge = (role) => {
    switch (role) {
      case 'primary_admin':
        return <span className="px-4 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded text-xs font-medium whitespace-nowrap">PRIMARY ADMIN</span>;
      case 'admin':
        return <span className="px-4 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-xs font-medium whitespace-nowrap">ADMIN</span>;
      case 'member':
        return <span className="px-4 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded text-xs font-medium whitespace-nowrap">MEMBER</span>;
      case 'viewer':
        return <span className="px-4 py-1 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded text-xs font-medium whitespace-nowrap">VIEWER</span>;
      case 'employee':
        // Backward compatibility: treat old 'employee' as 'member'
        return <span className="px-4 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded text-xs font-medium whitespace-nowrap">MEMBER</span>;
      default:
        return <span className="px-4 py-1 bg-gray-500/20 text-gray-400 border border-gray-500/30 rounded text-xs font-medium whitespace-nowrap">{role}</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="text-green-400 flex items-center gap-1"><Check size={14} /> Active</span>;
      case 'invited':
        return <span className="text-yellow-400 flex items-center gap-1"><Envelope size={14} /> Invited</span>;
      case 'disabled':
        return <span className="text-red-400 flex items-center gap-1"><X size={14} /> Disabled</span>;
      case null:
      case undefined:
      case '':
        // UsersThree authenticated via OAuth (Google, etc.) may not have status set
        // Treat them as active if they're in the system
        return <span className="text-green-400 flex items-center gap-1"><Check size={14} /> Active</span>;
      default:
        return <span className="text-gray-400">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="h-8 bg-gray-700 rounded w-64 mb-8 animate-pulse"></div>
          <div className="space-y-6">
            <div className="glass rounded-xl p-6 h-64 animate-pulse"></div>
            <div className="glass rounded-xl p-6 h-96 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  const seatsAvailable = (billing?.seats_total || 0) - (billing?.seats_used || 0);
  const isAtSeatLimit = seatsAvailable === 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <UsersThree size={32} className="text-[#6366F1]" />
              Team Members
            </h1>
            <p className="text-gray-400">Manage your organization&apos;s users and employee seats</p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 flex items-center gap-2"
          >
            <UserPlus size={18} />
            Invite Member
          </button>
        </div>

        {/* Seats at Limit Warning */}
        {isAtSeatLimit && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-3">
            <WarningCircle size={20} className="text-yellow-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-yellow-300 font-medium">All employee seats are assigned</p>
              <p className="text-yellow-400/80 text-sm mt-1">
                Purchase additional seats ($35/month each) to invite more employees.
              </p>
            </div>
          </div>
        )}

        {/* Seats & Billing Panel */}
        <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)] mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-white mb-2">Seats & Billing</h2>
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-[#6366F1]/20 text-[#6366F1] border border-[#6366F1]/30 rounded text-sm font-medium">
                  {billing?.plan_tier === 'basic' ? 'Basic Plan' : 
                   billing?.plan_tier === 'pro' ? 'Pro Plan' : 
                   billing?.plan_tier === 'starter' ? 'Starter Plan' : 'Partner Pro Plan'}
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowPurchasePanel(!showPurchasePanel)}
              className="px-5 py-2.5 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 transition-all shadow-lg shadow-[#6366F1]/25 flex items-center gap-2 font-medium"
            >
              <Plus size={18} />
              {showPurchasePanel ? 'Hide' : 'Add Seats'}
              {showPurchasePanel ? <CaretUp size={16} /> : <CaretDown size={16} />}
            </button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Included Seats</p>
              <p className="text-2xl font-bold text-white">{billing?.seats_included || 0}</p>
            </div>
            <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Purchased Seats</p>
              <p className="text-2xl font-bold text-white">{billing?.seats_purchased || 0}</p>
              <p className="text-xs text-gray-500 mt-1">$35/month each</p>
            </div>
            <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Total Seats</p>
              <p className="text-2xl font-bold text-white">{billing?.seats_total || 0}</p>
            </div>
            <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Seats Used</p>
              <p className="text-2xl font-bold text-white">{billing?.seats_used || 0}</p>
              <p className="text-xs text-gray-500 mt-1">{seatsAvailable} available</p>
            </div>
          </div>

          {/* Seat info note */}
          <div className="p-3 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.06)] rounded-lg">
            <p className="text-xs text-gray-500">All team members (admin and member roles) share the same seat pool.</p>
          </div>

          {/* Seat Purchase Panel */}
          {showPurchasePanel && (
            <div className="mt-6 pt-6 border-t border-[rgba(255,255,255,0.1)]">
              <h3 className="text-lg font-semibold text-white mb-4">Purchase Additional Seats</h3>
              
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm text-gray-400 mb-2">Number of Seats to Add</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSeatsToPurchase(Math.max(1, seatsToPurchase - 1))}
                      disabled={seatsToPurchase <= 1}
                      className="w-10 h-10 glass rounded-lg flex items-center justify-center text-white hover:bg-[rgba(255,255,255,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Minus size={18} />
                    </button>
                    <input
                      type="number"
                      value={seatsToPurchase}
                      onChange={(e) => setSeatsToPurchase(Math.max(1, parseInt(e.target.value) || 1))}
                      min="1"
                      className="w-24 px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-center focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                    />
                    <button
                      onClick={() => setSeatsToPurchase(seatsToPurchase + 1)}
                      className="w-10 h-10 glass rounded-lg flex items-center justify-center text-white hover:bg-[rgba(255,255,255,0.1)]"
                    >
                      <Plus size={18} />
                    </button>
                    <div className="ml-4">
                      <p className="text-sm text-gray-400">Monthly Subscription</p>
                      <p className="text-xl font-bold text-white">${seatsToPurchase * 35}/month</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    New total after purchase: {(billing?.seats_total || 0) + seatsToPurchase} seats
                  </p>
                </div>
                <button
                  onClick={handleUpdateSeats}
                  disabled={processingPayment || seatsToPurchase < 1}
                  className="px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                >
                  {processingPayment ? (
                    <>
                      <CircleNotch size={18} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard size={18} />
                      Purchase Seats
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                You&apos;ll be redirected to Stripe to complete your purchase. Seats are billed monthly and can be cancelled anytime.
              </p>
            </div>
          )}
        </div>

        {/* Team Members Table */}
        <div className="glass rounded-xl border border-[rgba(255,255,255,0.1)] overflow-hidden">
          <div className="px-6 py-4 border-b border-[rgba(255,255,255,0.1)] flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-lg font-semibold text-white" data-testid="team-members-heading">
              Team Members ({members.length})
            </h2>

            {/* Search & Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search name or email..."
                  className="pl-9 pr-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#6366F1] w-56"
                  data-testid="search-members-input"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#6366F1] appearance-none cursor-pointer"
                data-testid="filter-role"
              >
                <option value="all">All Roles</option>
                <option value="primary_admin">Owner</option>
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#6366F1] appearance-none cursor-pointer"
                data-testid="filter-status"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="invited">Invited</option>
                <option value="disabled">Disabled</option>
              </select>
              {(searchQuery || roleFilter !== 'all' || statusFilter !== 'all') && (
                <button
                  onClick={() => { setSearchQuery(''); setRoleFilter('all'); setStatusFilter('all'); }}
                  className="px-2 py-2 text-gray-400 hover:text-white transition-colors"
                  title="Clear filters"
                  data-testid="clear-filters-btn"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(255,255,255,0.03)]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Seat
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(255,255,255,0.1)]">
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      {members.length === 0 ? (
                        <>
                          <UsersThree size={48} className="text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400">No team members yet</p>
                          <button
                            onClick={() => setShowInviteModal(true)}
                            className="mt-4 px-6 py-2 bg-[#6366F1] text-white rounded-lg hover:opacity-90"
                          >
                            Invite Your First Member
                          </button>
                        </>
                      ) : (
                        <>
                          <MagnifyingGlass size={48} className="text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400">No members match your filters</p>
                          <button
                            onClick={() => { setSearchQuery(''); setRoleFilter('all'); setStatusFilter('all'); }}
                            className="mt-4 px-4 py-2 text-sm text-[#6366F1] hover:underline"
                          >
                            Clear filters
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((member) => (
                    <React.Fragment key={member.id}>
                      <tr
                        className="hover:bg-[rgba(255,255,255,0.03)] transition-colors"
                        data-testid={`member-row-${member.id}`}
                      >
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-white font-medium">{member.name || 'Unnamed'}</p>
                            <p className="text-sm text-gray-400">{member.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getRoleBadge(member.role)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {activeOrganization?.name ? (
                              <>
                                <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                                <span className="text-sm text-gray-300">{cleanOrgName(activeOrganization.name)}</span>
                              </>
                            ) : (
                              <span className="text-sm text-gray-500">Unknown</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {getStatusBadge(member.status)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {(member.role === 'member' || member.role === 'viewer' || member.role === 'employee') ? (
                            member.seat_active ? (
                              <span className="text-green-400 flex items-center gap-1">
                                <Check size={14} /> Active
                              </span>
                            ) : (
                              <span className="text-gray-400">Not assigned</span>
                            )
                          ) : (
                            <span className="text-gray-500 text-xs">N/A (Admin)</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Manage Button - Opens Detail Drawer */}
                            <button
                              onClick={() => setDrawerMember(member)}
                              className="px-3 py-1 text-sm bg-[#6366F1]/10 text-[#6366F1] border border-[#6366F1]/20 rounded hover:bg-[#6366F1]/20 flex items-center gap-1 font-medium transition-colors"
                              title="Manage role, permissions & access"
                              data-testid={`manage-btn-${member.id}`}
                            >
                              <GearSix size={14} />
                              Manage
                            </button>
                            
                            {/* Invite management for pending members */}
                            {member.status === 'invited' && (
                              <button
                                onClick={() => handleRevokeInvite(member.id, member.email)}
                                disabled={revokingId === member.id}
                                className="px-3 py-1 text-sm bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded hover:bg-yellow-500/20 flex items-center gap-1"
                                title="Revoke invitation"
                                data-testid={`revoke-invite-btn-${member.id}`}
                              >
                                {revokingId === member.id ? <CircleNotch size={14} className="animate-spin" /> : <X size={14} />}
                                Revoke
                              </button>
                            )}
                            
                            {(member.role === 'member' || member.role === 'viewer' || member.role === 'employee') && !member.seat_active && member.status !== 'disabled' && (
                              <button
                                onClick={() => handleAssignSeat(member.id)}
                                disabled={isAtSeatLimit}
                                className="px-3 py-1 text-sm bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid={`assign-seat-btn-${member.id}`}
                              >
                                Assign Seat
                              </button>
                            )}
                            {(member.role === 'member' || member.role === 'viewer' || member.role === 'employee') && member.seat_active && (
                              <span className="px-3 py-1 text-sm bg-green-500/20 text-green-400 border border-green-500/30 rounded flex items-center gap-1">
                                <Check size={14} />
                                Seat Active
                              </span>
                            )}
                            {/* Trash button - Hidden for Primary User (Owner) - Auto-removes seat */}
                            {member.role !== 'primary_admin' && (
                              <button
                                onClick={() => handleDeleteUser(member.id, member.name, member.email)}
                                className="px-3 py-1 text-sm bg-red-500/20 text-red-400 border border-red-500/30 rounded hover:bg-red-500/30 flex items-center gap-1"
                                title="Remove user from organization (seat will be automatically freed)"
                                data-testid={`delete-user-btn-${member.id}`}
                              >
                                <Trash size={14} />
                                Trash
                              </button>
                            )}
                            {/* Owner badge for Primary User */}
                            {member.role === 'primary_admin' && (
                              <span className="px-3 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded text-xs font-medium flex items-center gap-1">
                                <Shield size={12} />
                                OWNER
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInvite}
        billing={billing}
        loading={inviting}
      />

      {/* Member Detail Drawer */}
      <MemberDetailDrawer
        member={drawerMember}
        isOpen={!!drawerMember}
        onClose={() => setDrawerMember(null)}
        onRefresh={() => {
          fetchTeamData();
          // Re-fetch the updated member for the drawer
          if (drawerMember) {
            setTimeout(() => {
              const updated = members.find((m) => m.id === drawerMember.id);
              if (updated) setDrawerMember(updated);
            }, 500);
          }
        }}
        currentUserId={currentUser?.id}
      />
    </div>
  );
};

export default TeamMembers;
